import { useCallback, useMemo, useRef, useState } from "react";
import { Input, Select } from "antd";
import {
  DEFAULT_TIMELINE_EASING,
  formatTimelineBezierValue,
  TIMELINE_EASING_LABELS,
  TIMELINE_EASING_PRESETS,
  type TimelineBezierValue,
  type TimelineEasingPreset,
} from "./utils/keyframeSegmentHelpers";

const CURVE_SIZE = 220;
const CURVE_PADDING = 22;
const HANDLE_Y_OVERSHOOT = 0.6;

interface KeyframeEasingEditorProps {
  preset: TimelineEasingPreset;
  bezier: TimelineBezierValue;
  onPresetChange: (preset: TimelineEasingPreset) => void;
  onBezierChange: (bezier: TimelineBezierValue) => void;
}

type DraggedHandle = "start" | "end" | null;

const clampUnit = (value: number) => Math.max(0, Math.min(1, value));
const clampRange = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const MIN_HANDLE_Y = -HANDLE_Y_OVERSHOOT;
const MAX_HANDLE_Y = 1 + HANDLE_Y_OVERSHOOT;

const toCanvasPoint = (x: number, y: number) => {
  const innerSize = CURVE_SIZE - CURVE_PADDING * 2;
  const normalizedY = (MAX_HANDLE_Y - y) / (MAX_HANDLE_Y - MIN_HANDLE_Y);

  return {
    x: CURVE_PADDING + x * innerSize,
    y: CURVE_PADDING + normalizedY * innerSize,
  };
};

const fromCanvasPoint = (x: number, y: number): { x: number; y: number } => {
  const innerSize = CURVE_SIZE - CURVE_PADDING * 2;
  const normalizedY = 1 - (y - CURVE_PADDING) / innerSize;

  return {
    x: clampUnit((x - CURVE_PADDING) / innerSize),
    y: MIN_HANDLE_Y + normalizedY * (MAX_HANDLE_Y - MIN_HANDLE_Y),
  };
};

export function KeyframeEasingEditor({
  preset,
  bezier,
  onPresetChange,
  onBezierChange,
}: KeyframeEasingEditorProps) {
  const graphRef = useRef<HTMLDivElement | null>(null);
  const [draggedHandle, setDraggedHandle] = useState<DraggedHandle>(null);

  const currentBezier = preset === "hold" ? DEFAULT_TIMELINE_EASING : bezier;
  const startPoint = toCanvasPoint(0, 0);
  const endPoint = toCanvasPoint(1, 1);
  const outHandle = toCanvasPoint(currentBezier.x1, currentBezier.y1);
  const inHandle = toCanvasPoint(currentBezier.x2, currentBezier.y2);
  const startGuideY = endPoint.y;
  const endGuideY = startPoint.y;

  const pathD = useMemo(
    () =>
      `M ${startPoint.x} ${startPoint.y} C ${outHandle.x} ${outHandle.y}, ${inHandle.x} ${inHandle.y}, ${endPoint.x} ${endPoint.y}`,
    [endPoint.x, endPoint.y, inHandle.x, inHandle.y, outHandle.x, outHandle.y, startPoint.x, startPoint.y]
  );

  const updateHandleFromPointer = useCallback(
    (handle: Exclude<DraggedHandle, null>, clientX: number, clientY: number) => {
      const bounds = graphRef.current?.getBoundingClientRect();
      if (!bounds) return;

      const nextPoint = fromCanvasPoint(clientX - bounds.left, clientY - bounds.top);
      if (handle === "start") {
        onBezierChange({
          ...currentBezier,
          x1: nextPoint.x,
          y1: clampRange(nextPoint.y, MIN_HANDLE_Y, 1),
        });
        return;
      }

      onBezierChange({
        ...currentBezier,
        x2: nextPoint.x,
        y2: clampRange(nextPoint.y, 0, MAX_HANDLE_Y),
      });
    },
    [currentBezier, onBezierChange]
  );

  const startHandleDrag = useCallback(
    (handle: Exclude<DraggedHandle, null>) =>
      (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggedHandle(handle);

        const onMouseMove = (nextEvent: MouseEvent) => {
          updateHandleFromPointer(handle, nextEvent.clientX, nextEvent.clientY);
        };

        const onMouseUp = () => {
          setDraggedHandle(null);
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
      },
    [updateHandleFromPointer]
  );

  const selectOptions = useMemo(
    () => [
      ...Object.keys(TIMELINE_EASING_PRESETS).map((key) => ({
        label: TIMELINE_EASING_LABELS[key as TimelineEasingPreset],
        value: key,
      })),
      {
        label: TIMELINE_EASING_LABELS.hold,
        value: "hold",
      },
      {
        label: TIMELINE_EASING_LABELS.custom,
        value: "custom",
      },
    ],
    []
  );

  return (
    <div
      className="timeline-easing-editor"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="timeline-easing-editor__header">
        <span>Easing</span>
        <Select
          size="small"
          value={preset}
          options={selectOptions}
          onChange={(nextPreset) => onPresetChange(nextPreset as TimelineEasingPreset)}
        />
      </div>

      <div
        ref={graphRef}
        className={`timeline-easing-editor__graph ${
          draggedHandle ? "timeline-easing-editor__graph--dragging" : ""
        }`}
      >
        <svg width={CURVE_SIZE} height={CURVE_SIZE} viewBox={`0 0 ${CURVE_SIZE} ${CURVE_SIZE}`}>
          <line
            x1={CURVE_PADDING}
            y1={startGuideY}
            x2={CURVE_SIZE - CURVE_PADDING}
            y2={startGuideY}
            className="timeline-easing-editor__guide"
          />
          <line
            x1={CURVE_PADDING}
            y1={endGuideY}
            x2={CURVE_SIZE - CURVE_PADDING}
            y2={endGuideY}
            className="timeline-easing-editor__guide"
          />
          <line
            x1={startPoint.x}
            y1={startPoint.y}
            x2={outHandle.x}
            y2={outHandle.y}
            className="timeline-easing-editor__handle-line"
          />
          <line
            x1={endPoint.x}
            y1={endPoint.y}
            x2={inHandle.x}
            y2={inHandle.y}
            className="timeline-easing-editor__handle-line"
          />
          <path d={pathD} className="timeline-easing-editor__curve" />
          <circle
            cx={startPoint.x}
            cy={startPoint.y}
            r={7}
            className="timeline-easing-editor__anchor"
          />
          <circle
            cx={endPoint.x}
            cy={endPoint.y}
            r={7}
            className="timeline-easing-editor__anchor"
          />
          {preset !== "hold" && (
            <>
              <circle
                cx={outHandle.x}
                cy={outHandle.y}
                r={9}
                className="timeline-easing-editor__handle"
                onMouseDown={startHandleDrag("start")}
              />
              <circle
                cx={inHandle.x}
                cy={inHandle.y}
                r={9}
                className="timeline-easing-editor__handle"
                onMouseDown={startHandleDrag("end")}
              />
            </>
          )}
        </svg>
      </div>

      <div className="timeline-easing-editor__value-row">
        <span>Value</span>
        <Input
          size="small"
          readOnly
          value={
            preset === "hold"
              ? TIMELINE_EASING_LABELS.hold
              : formatTimelineBezierValue(currentBezier)
          }
        />
      </div>
    </div>
  );
}
